import { DatePipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { EventEmitter, inject, Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DomSanitizer } from '@angular/platform-browser'
import { Router } from '@angular/router'
import { LangChangeEvent, TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { cnpj, cpf } from 'cpf-cnpj-validator'
import { Observable } from 'rxjs'
import { LoaderModalComponent } from 'src/app/shared/components/loader-modal/loader-modal.component'
import { InterceptorSkipHeader } from 'src/interceptors/token.interceptor'
import { KitType, OriginTypes } from 'src/store/quote/quote.meta.model'
import { QuoteMetaState } from 'src/store/quote/quote.meta.state'
import Swal, { SweetAlertResult } from 'sweetalert2'
import { IChoice } from '../interfaces/question/question.interface'
import { RolesType } from 'src/enums/roles.enum'
import { FreightType } from 'src/enums/quotation.enum'

export interface ControlParams {
  orderByDirection?: string
  orderBy?: string
  type?: string
  page?: number
  limit?: number
  roleType?: string
  status?: string
  search?: string
  startDate?: string
  endDate?: string
  paymentMethod?: string
}

export const TypesMatrix = {
  PANEL: 'panels',
  INVERTER: 'inverters',
  BRACKET: 'brackets',
  PROTECTION: 'protections',
  CONNECTOR: 'connectors',
  CABLE: 'cables',
  ACCESSORY: 'accessories',
}

@Injectable({
  providedIn: 'root',
})
export class Utils {
  private http = inject(HttpClient)
  private router = inject(Router)
  private datePipe = inject(DatePipe)
  private translate = inject(TranslateService)
  private sanitizer = inject(DomSanitizer)
  private snackBar = inject(MatSnackBar)
  private store = inject(Store)
  private dialog = inject(MatDialog)

  showSuccessMessage(
    title: string,
    text?: string,
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text || ''),
      icon: 'success',
      showCancelButton: false,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ok',
    })
  }

  showErrorMessage(
    title: string,
    text?: string,
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text || ''),
      icon: 'error',
      showCancelButton: false,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Ok',
    })
  }

  showWarningMessage(
    title: string,
    text?: string,
    props?: { showCloseButton: boolean, allowOutsideClick: boolean },
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text || ''),
      icon: 'warning',
      showCancelButton: false,
      confirmButtonColor: '#53689c',
      confirmButtonText: 'Ok',
      showCloseButton: props?.showCloseButton ?? false,
      allowOutsideClick: props?.allowOutsideClick ?? true,
    })
  }

  showWarningMessageWithHtml(
    title: string,
    html: string,
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      html: this.getTranslatedKey(html),
      icon: 'warning',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Vincular',
      cancelButtonText: 'Cancelar',
      showCancelButton: true,
    })
  }

  showConfirmMessage(
    title: string,
    text?: string,
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text || ''),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#eadbc9',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    })
  }

  showConfirmMessageWithInput(
    title: string,
    text: string,
    placeholder: string,
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text),
      input: 'text',
      inputPlaceholder: placeholder,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    })
  }

  showConfirmMessageWithSelect(
    title: string,
    text: string,
    options: { [key: number]: string },
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text),
      input: 'select',
      inputOptions: options,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    })
  }

  showAlertWithRadios(
    title: string,
    text: string,
    options: { [key: number]: string },
  ): Promise<SweetAlertResult<any>> {
    return Swal.fire({
      title: this.getTranslatedKey(title),
      text: this.getTranslatedKey(text),
      input: 'radio',
      inputOptions: options,
      inputValue: 1,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
    })
  }

  convertBrDateToEnType(date: string): string {
    const [day, month, year] = date.split('/')
    return `${month}/${day}/${year}`
  }

  spreadValueFromObject(object: any, key: string): any {
    if (object._isAMomentObject) {
      return object.format('L')
    }

    return object[key] || ''
  }

  getAddressByCep(cep: string): Observable<any> {
    return this.http.get(`https://viacep.com.br/ws/${cep}/json/`, {
      headers: InterceptorSkipHeader,
    })
  }

  getCitiesByState(state: string): Observable<any> {
    return this.http.get(
      `https://servicodados.ibge.gov.br/api/v1/localidades/estados/${state}/municipios`,
      { headers: InterceptorSkipHeader },
    )
  }

  getBrazilianCities(): Observable<any> {
    return this.http.get(
      `https://servicodados.ibge.gov.br/api/v1/localidades/municipios?orderBy=nome`,
      { headers: InterceptorSkipHeader },
    )
  }

  getCoordinatesByCity(city: string): Promise<any> {
    return this.http
      .get(
        `https://nominatim.openstreetmap.org/search?city=${city}&format=json`,
        { headers: InterceptorSkipHeader },
      )
      .toPromise()
  }

  getOnlyCitiesNamesWithState(cities: any[]): any[] {
    return cities.map((city) => {
      return {
        name: `${city.nome} - ${city.microrregiao.mesorregiao.UF.sigla}`,
      }
    })
  }

  normalizeCep(cep: string): string {
    return cep.replace(/\D/g, '')
  }

  navigateTo(route: string): void {
    this.router.navigate([route])
  }

  getCurrentRoute(): string {
    return this.router.url
  }

  filterValue(value: string, options: IChoice[]): IChoice[] {
    if (value && options) {
      const filterValue = value.toLowerCase()
      return options.filter((option) =>
        option.title.toString().toLowerCase().includes(filterValue),
      )
    }

    return []
  }

  parsePhoneNumber(phoneNumberString: string): string {
    if (!phoneNumberString) {
      return ''
    }
    return phoneNumberString.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }

  parseDateFromApi(date: string): Date | null {
    if (!date) {
      return null
    }
    const parsedDate = new Date(date)
    parsedDate.setHours(parsedDate.getHours() + 3)
    return parsedDate
  }

  formatIdentification(identification: string): string {
    if (!identification || identification.length < 11) {
      return ''
    }
    return identification.length === 14
      ? cnpj.format(identification)
      : cpf.format(identification)
  }

  normalizeCpf(cpf: string): string {
    return cpf.replace(/\D/g, '')
  }

  normalizePhoneNumber(phoneNumber: string): string {
    return phoneNumber.replace(/\D/g, '')
  }

  parseDateToApiFormat(date: Date): string | null {
    return this.datePipe.transform(date, 'yyyy-MM-dd')
  }

  removeEmptyProperties(object: any) {
    const newObject = { ...object }
    Object.keys(newObject).forEach(
      (key) =>
        (newObject[key] === undefined ||
          newObject[key] === null ||
          newObject[key] === '') &&
        delete newObject[key],
    )
    return newObject
  }

  getErrorMessage(error: any): string {
    if (error.error?.exception?.response?.message) {
      return error.error?.exception?.response?.message
    }
    if (error.error?.exception?.message) {
      return error.error.exception.message
    }
    if (error.error?.message) {
      return error.error.message
    }
    if (error?.error?.errors) {
      return error.error.errors[0].message
    }
    return error?.error?.error
  }

  isIdentificationValid(identification: string): boolean {
    if (!identification) {
      return false
    }
    return identification.length === 14
      ? cnpj.isValid(identification)
      : cpf.isValid(identification)
  }

  isEmailValid(email: string): boolean {
    if (!email) {
      return false
    }
    const re = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/
    return re.test(email)
  }

  parseValueToBRLCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  parseBRLCurrencyToValue(value: string): number {
    return Number(value.replace(/\D/g, '')) / 100
  }

  getTranslatedKey(key: string): string {
    if (!key) {
      return ''
    }
    return this.translate.instant(key)
  }

  watchTranslationChanges(): EventEmitter<LangChangeEvent> {
    return this.translate.onLangChange
  }

  removeNullValues(object: any): any {
    const newObject = { ...object }
    Object.keys(newObject).forEach(
      (key) => newObject[key] === null && delete newObject[key],
    )
    return newObject
  }

  parseDataToChoiceModel(
    data: any[],
    titleKey: string,
    valueKey: string,
  ): IChoice[] {
    return data.map((item) => ({
      title: item[titleKey],
      value: item[valueKey],
    }))
  }

  getIdFromUrl(splitCount: number): string {
    return this.router.url.split('/')[splitCount]
  }

  sanitizeFile(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url)
  }

  generateRandomString(length: number): string {
    let result = ''
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
    const charactersLength = characters.length
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength))
    }
    return result
  }

  copyToClipboard(array: any[], keys: string[]): void {
    const parsedContent = array.map((item) => {
      return `
        ${keys.map((key) => `${key}: ${item[key]}`).join('\n')}
      `
    })
    navigator.clipboard.writeText(parsedContent.join('\n'))
    this.snackBar.open('Copied to clipboard', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    })
  }

  copyCSVToClipboard(array: any[], keys: string[]): void {
    const csv = this.convertToCSV(array, keys)
    navigator.clipboard.writeText(csv)
    this.snackBar.open('Copied to clipboard', 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    })
  }

  downloadCSV(array: any[], keys: string[], filename: string): void {
    const csv = this.convertToCSV(array, keys)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.setAttribute('href', url)
    link.setAttribute('download', `${filename}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    this.snackBar.open(`Downloaded ${filename}.csv succesfully`, 'Close', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    })
  }

  convertToCSV(objArray: any[], keys: string[]): string {
    const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray
    let str = ''
    let row = ''

    for (const index in keys) {
      row += keys[index] + ','
    }
    row = row.slice(0, -1)
    str += row + '\r\n'

    for (const element of array) {
      let line = ''
      for (const index in keys) {
        const head = keys[index]
        line += element[head] + ','
      }
      line = line.slice(0, -1)
      str += line + '\r\n'
    }
    return str
  }

  generateTotvs(products: any[], panelPotency: number) {
    const itemsModel = this.store
      .selectSnapshot(QuoteMetaState.getSelectedItemsTypeModel)
      .toLowerCase()
    const selectedPanels = this.store.selectSnapshot(
      QuoteMetaState.getSelectedPanel,
    )

    if (
      itemsModel === 'byd panel national' ||
      itemsModel === 'byd panel imported'
    ) {
      return selectedPanels.code
    }
    const operationModeCode = this.generateOperationModeCode()

    const modelCode =
      itemsModel === 'full kit byd national' ||
      itemsModel === 'full kit byd imported'
        ? 'C'
        : 'S'

    const origin = selectedPanels.origin

    const originCode = origin === OriginTypes.NATIONAL ? 'N' : 'I'
    const outputPotency = this.generateOutputPotency(products)
    return `${operationModeCode}${modelCode}BYD-${outputPotency}${panelPotency}${originCode}`
  }

  calculateShippingCost(freightType: FreightType): number {
    if (freightType === 'FOB') return 0
    const equipmentPrice = this.store.selectSnapshot(
      QuoteMetaState.getEquipmentPrice,
    )
    if (freightType === 'CIF')
      return parseFloat((equipmentPrice / 0.97 - equipmentPrice).toFixed(2))
    return 0
  }

  calculateProposalDaysRemaining(createdAt: string): number {
    const createdAtDate = new Date(createdAt)
    const today = new Date()
    const differenceInMilliseconds = today.getTime() - createdAtDate.getTime()
    const aDayInMilliseconds = 24 * 60 * 60 * 1000
    const differenceInDays = Math.floor(
      differenceInMilliseconds / aDayInMilliseconds,
    )
    const remaningDays = 14 - differenceInDays
    return remaningDays <= 0 ? 0 : remaningDays
  }

  showLoader(params?: any) {
    const { width, height, data, disableClose } = params || {
      width: '500px',
      height: '300px',
      data: 'Wait while we load some information...',
      disableClose: false,
    }
    const loader = this.dialog.open(LoaderModalComponent, {
      width,
      height,
      panelClass: 'main-modal',
      disableClose,
      data: this.getTranslatedKey(data),
    })
    return loader
  }

  showToast(message: string, action: string, duration: number) {
    this.snackBar.open(message, action, {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    })
  }

  parseBRLDateToDateObject(date: any): string {
    if (!date) return ''
    const splitedDate = date.split('/')
    const newDate =
      date.indexOf('/') !== -1
        ? new Date(splitedDate[2], splitedDate[1] - 1, splitedDate[0])
        : date
    return newDate
  }

  formatCPFOrCNPJ(value: string): string {
    const cleanedValue = value.replace(/[^\d]/g, '')
    if (cleanedValue.length === 11) {
      return cleanedValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    } else if (cleanedValue.length === 14) {
      return cleanedValue.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5',
      )
    }

    return value
  }

  formatAddress(address: any): string {
    const { street, number, complement, neighborhood, city, state } =
      address ?? {}
    const addressString = `${street}, ${number} ${
      complement ? '- ' + complement : ''
    }, ${neighborhood}, ${city} - ${state}`
    return addressString
  }

  formatPhone(phone: string): string {
    if (!phone) return ''

    const cleanedPhone = phone.replace(/[^\d]/g, '')
    if (cleanedPhone.length === 11) {
      return cleanedPhone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    } else if (cleanedPhone.length === 10) {
      return cleanedPhone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
    }

    return phone
  }

  private generateOutputPotency(products: any[]) {
    const outputPower = products.reduce((acc, product) => {
      if (product.type === 'INVERTER') {
        acc += product.quantity * product.potency
      }
      return acc
    }, 0)
    const measures = +outputPower < 1000 ? 'K' : 'M'
    const outputPowerConvert =
      measures === 'K' ? `${outputPower}` : `${Math.round(outputPower / 1000)}`
    let outputPowerCode = ''
    if (outputPowerConvert.length === 1) {
      outputPowerCode = `000${outputPowerConvert}`
    } else if (outputPowerConvert.length === 2) {
      outputPowerCode = `00${outputPowerConvert}`
    } else if (outputPowerConvert.length === 3) {
      outputPowerCode = `0${outputPowerConvert}`
    } else {
      outputPowerCode = `${outputPowerConvert}`
    }
    return `${outputPowerCode}${measures}`
  }

  private generateOperationModeCode() {
    const inverters: any = this.store.selectSnapshot(
      QuoteMetaState.getSelectedInverters,
    )
    if (inverters.length > 0) {
      const inverterOperationModesEquals = inverters.every(
        (obj: any, index: any, array: any) =>
          obj.inverterOperationMode === array[0].inverterOperationMode,
      )
      if (inverterOperationModesEquals) {
        const inverterOperationMode = inverters[0].inverterOperationMode
        if (
          inverterOperationMode === 'ON_GRID' ||
          inverterOperationMode === null
        ) {
          return 'G'
        } else if (inverterOperationMode === 'OFF_GRID') {
          return 'GO'
        } else if (inverterOperationMode === 'HYBRID') {
          return 'GH'
        }
      } else {
        return 'GM'
      }
    }
    return 'G'
  }

  isExternalUser(userRoles: RolesType[]) {
    const externalRoles = [
      RolesType.END_CLIENT,
      RolesType.INTEGRATOR,
      RolesType.PARTNER,
      RolesType.DISTRIBUTOR,
      RolesType.TEMPORARY_ACCESS,
    ]
    return userRoles.some((role) => externalRoles.includes(role))
  }

  getKitType(orderData: { isKit?: boolean; kitType?: KitType }): KitType {
    if (!orderData.kitType) {
      return orderData.isKit ? KitType.STANDARD : KitType.CUSTOM
    }

    return orderData.kitType
  }

  getCheckoutKitType(): KitType {
    const checkoutDataKitType = this.store.selectSnapshot(
      QuoteMetaState.getCheckoutDataKitType,
    )

    return checkoutDataKitType || KitType.CUSTOM
  }

  getProposalDataKitType(): KitType {
    const proposalData = this.store.selectSnapshot(
      QuoteMetaState.getProposalData,
    )

    return this.getKitType({
      isKit: proposalData.orderKitContents![0]?.isKit,
      kitType: proposalData.orderKitContents![0]?.kitType,
    })
  }

  getControlParams({
    params,
    status,
    search,
    startDate,
    endDate,
    paymentMethod,
    type,
  }: {
    params: ControlParams
    status?: string
    search?: string
    startDate?: string
    endDate?: string
    paymentMethod?: string
    type?: string
  }) {
    if (type) {
      params.type = type
    }
    if (status !== null && status !== '') {
      params.status = status
    }
    if (search !== null && search !== '') {
      params.search = search
    }
    if (startDate !== '' || endDate !== '') {
      params.startDate = startDate
      params.endDate = endDate
    }
    if (paymentMethod !== null && paymentMethod !== '') {
      params.paymentMethod = paymentMethod
    }
  }

  parseDateISOToBRLDate(date: string): string {
    if (!date) return ''

    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeZone: 'UTC',
    }).format(new Date(date))
  }
}
